import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';

import { activeLineClass, renderInlineNodes, renderLine } from '../lib/markdown';

// Drives the `photo:` property cell: an "Add photo" affordance when empty, and
// a "pick new" overlay on the image when set. The owner (DailyNoteEditor) holds
// the actual picker logic; this generic editor just wires the cell to it.
export interface PhotoControl {
  onPick: () => void;
  busy: boolean;
  needsConnect: boolean;
  message: string | null;
}

interface Props {
  value: string;
  onChange: (v: string) => void;
  autoFocus?: boolean;
  className?: string;
  photo?: PhotoControl;
}

type Caret = number | 'start' | 'end';

// Obsidian-style live-preview editor. The document is a list of source lines;
// every line renders as formatted markdown except the one the cursor is on,
// which becomes a raw <textarea> so the formatting characters are visible and
// editable. Moving the caret off a line (arrows, click, Enter) re-renders it.
//
// Most lines stand alone, but a few constructs span lines and render as one box
// — YAML frontmatter (the "properties" panel), `> [!type]` callouts, and fenced
// code. Those are grouped into segments; when the cursor lands inside such a
// block its lines fall back to raw source so you can edit them.
//
// Editing reliability comes from there being exactly one real <textarea> (the
// active line) — native selection/IME/paste within a line just works. We only
// hand-roll the cross-line motions: Enter splits, Backspace/Delete merge, and
// arrows at a line edge hop to the neighbour.
export default function LiveMarkdownEditor({
  value,
  onChange,
  autoFocus,
  className,
  photo
}: Props) {
  const lines = value.split('\n');
  const [active, setActive] = useState<number | null>(null);
  const activeRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const pendingCaret = useRef<Caret | null>(null);
  const didAutoFocus = useRef(false);

  const segments = useMemo(() => parseSegments(lines), [value]);

  function activate(i: number, caret: Caret = 'end') {
    pendingCaret.current = caret;
    setActive(i);
  }

  useLayoutEffect(() => {
    if (autoFocus && !didAutoFocus.current) {
      didAutoFocus.current = true;
      activate(lines.length - 1, 'end');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocus]);

  useLayoutEffect(() => {
    const ta = activeRef.current;
    if (!ta) return;
    const c = pendingCaret.current;
    if (c != null) {
      ta.focus();
      const pos = c === 'start' ? 0 : c === 'end' ? ta.value.length : Math.min(c, ta.value.length);
      ta.setSelectionRange(pos, pos);
      pendingCaret.current = null;
    }
    autosize(ta);
  }, [active, value]);

  function commit(next: string[]) {
    onChange(next.join('\n'));
  }

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>, i: number) {
    const v = e.target.value;
    if (v.includes('\n')) {
      const parts = v.split('\n');
      activate(i + parts.length - 1, 'end');
      commit([...lines.slice(0, i), ...parts, ...lines.slice(i + 1)]);
    } else {
      const next = [...lines];
      next[i] = v;
      commit(next);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>, i: number) {
    const ta = e.currentTarget;
    const { selectionStart: s, selectionEnd: en, value: v } = ta;
    const atStart = s === 0 && en === 0;
    const atEnd = s === v.length && en === v.length;

    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      activate(i + 1, 'start');
      commit([...lines.slice(0, i), v.slice(0, s), v.slice(en), ...lines.slice(i + 1)]);
    } else if (e.key === 'Backspace' && atStart && i > 0) {
      e.preventDefault();
      const prev = lines[i - 1];
      activate(i - 1, prev.length);
      commit([...lines.slice(0, i - 1), prev + v, ...lines.slice(i + 1)]);
    } else if (e.key === 'Delete' && atEnd && i < lines.length - 1) {
      e.preventDefault();
      activate(i, v.length);
      commit([...lines.slice(0, i), v + lines[i + 1], ...lines.slice(i + 2)]);
    } else if ((e.key === 'ArrowUp' || (e.key === 'ArrowLeft' && atStart)) && atStart && i > 0) {
      e.preventDefault();
      activate(i - 1, 'end');
    } else if ((e.key === 'ArrowDown' || (e.key === 'ArrowRight' && atEnd)) && atEnd && i < lines.length - 1) {
      e.preventDefault();
      activate(i + 1, 'start');
    }
  }

  function handleBlur(e: React.FocusEvent<HTMLTextAreaElement>) {
    const next = e.relatedTarget as Node | null;
    if (next && containerRef.current?.contains(next)) return;
    setActive(null);
  }

  // The one real editable: a raw <textarea> for the active line. `extra` styles
  // it to match the block it sits in (heading size, monospace code, etc.).
  function activeTextarea(i: number, extra?: string) {
    return (
      <textarea
        ref={activeRef}
        rows={1}
        value={lines[i]}
        spellCheck={false}
        onChange={(e) => handleChange(e, i)}
        onKeyDown={(e) => handleKeyDown(e, i)}
        onBlur={handleBlur}
        onInput={(e) => autosize(e.currentTarget)}
        className={clsx(
          'block w-full resize-none overflow-hidden border-none bg-transparent p-0 leading-7 text-[var(--text)] outline-none',
          extra
        )}
      />
    );
  }

  function activateProps(e: React.MouseEvent, i: number) {
    e.preventDefault();
    activate(i, 'end');
  }

  function renderSegment(seg: Segment) {
    if (seg.kind === 'frontmatter') {
      const editing = active != null && active >= seg.start && active <= seg.end;
      if (editing) {
        return (
          <div key={`fm-${seg.start}`} className="my-1 font-mono text-[0.82rem] text-[var(--text-muted)]">
            {range(seg.start, seg.end).map((i) =>
              i === active ? (
                <div key={i}>{activeTextarea(i, 'font-mono text-[0.82rem]')}</div>
              ) : (
                <div key={i} className="cursor-text whitespace-pre-wrap" onMouseDown={(e) => activateProps(e, i)}>
                  {lines[i] || ' '}
                </div>
              )
            )}
          </div>
        );
      }
      const props = parseFrontmatter(lines, seg.start + 1, seg.end - 1);
      return (
        <div
          key={`fm-${seg.start}`}
          className="mb-4 flex flex-col gap-1 border-b border-[var(--border)] pb-3 text-[0.85rem]"
        >
          {props.map((p) => (
            <div
              key={p.lineIndex}
              className="flex cursor-text items-start gap-3 rounded px-1 py-[2px] hover:bg-[var(--bg-hover)]"
              onMouseDown={(e) => activateProps(e, p.lineIndex)}
            >
              <span className="w-24 flex-shrink-0 text-[var(--text-faint)]">{p.key}</span>
              <span className="min-w-0 flex-1 text-[var(--text-muted)]">
                <PropertyValue prop={p} photo={photo} />
              </span>
            </div>
          ))}
        </div>
      );
    }

    if (seg.kind === 'callout') {
      return (
        <div
          key={`co-${seg.start}`}
          className="my-3 overflow-hidden rounded-md border border-[var(--border-strong)] bg-[var(--bg-muted)]"
        >
          {range(seg.start, seg.end).map((i) => {
            const inner = lines[i].replace(/^>\s?/, '');
            if (i === seg.start) {
              // header row: "✏ Title"
              return (
                <div key={i} className="px-3 pt-2">
                  {i === active ? (
                    activeTextarea(i, 'font-semibold text-[var(--accent)]')
                  ) : (
                    <div
                      className="flex cursor-text items-center gap-2 font-semibold text-[var(--accent)]"
                      onMouseDown={(e) => activateProps(e, i)}
                    >
                      <PencilIcon />
                      <span>{seg.title}</span>
                    </div>
                  )}
                </div>
              );
            }
            return (
              <div key={i} className={clsx('px-3 text-[var(--text-muted)]', i === seg.end && 'pb-2')}>
                {i === active ? (
                  activeTextarea(i)
                ) : (
                  <div className="cursor-text" onMouseDown={(e) => activateProps(e, i)}>
                    {renderLine(inner)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    if (seg.kind === 'fence') {
      return (
        <div key={`fc-${seg.start}`} className="my-2 rounded-md bg-[var(--bg-muted)] px-3 py-2">
          {range(seg.start, seg.end).map((i) =>
            i === active ? (
              <div key={i}>{activeTextarea(i, 'font-mono text-[0.85em]')}</div>
            ) : (
              <div
                key={i}
                className="cursor-text whitespace-pre-wrap break-words font-mono text-[0.85em] text-[var(--text-muted)]"
                onMouseDown={(e) => activateProps(e, i)}
              >
                {lines[i] || ' '}
              </div>
            )
          )}
        </div>
      );
    }

    // plain line
    const i = seg.index;
    return (
      <div key={i} className={clsx('first:mt-0', lineSpacing(lines[i]))}>
        {i === active ? (
          activeTextarea(i, activeLineClass(lines[i]))
        ) : (
          <div className="cursor-text whitespace-pre-wrap break-words" onMouseDown={(e) => activateProps(e, i)}>
            {renderLine(lines[i])}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={clsx(
        'overflow-y-auto rounded-md border border-[var(--border)] bg-[var(--bg)] px-4 py-3 text-[0.92rem] leading-7 text-[var(--text)] focus-within:border-[var(--accent)]',
        className
      )}
      onMouseDown={(e) => {
        if (e.target === containerRef.current) {
          e.preventDefault();
          activate(lines.length - 1, 'end');
        }
      }}
    >
      {segments.map(renderSegment)}
    </div>
  );
}

function PropertyValue({ prop, photo }: { prop: FrontmatterProp; photo?: PhotoControl }) {
  if (prop.items.length > 0) {
    return (
      <span className="flex flex-wrap gap-1">
        {prop.items.map((it, k) => (
          <span key={k} className="rounded bg-[var(--bg-hover)] px-[6px] py-[1px] text-[0.8rem] text-[var(--accent)]">
            {it}
          </span>
        ))}
      </span>
    );
  }
  const val = unquote(prop.value);
  if (prop.key.toLowerCase() === 'photo') return <PhotoProperty value={val} photo={photo} />;
  if (!val) return <span className="italic text-[var(--text-faint)]">empty</span>;
  return <>{renderInlineNodes(val)}</>;
}

// stopPropagation throughout so clicking the image/controls doesn't trip the
// property row's click-to-edit-source handler.
function PhotoProperty({ value, photo }: { value: string; photo?: PhotoControl }) {
  if (value) {
    return (
      <span className="relative inline-block" onMouseDown={(e) => e.stopPropagation()}>
        <img
          src={assetSrc(value)}
          alt="photo"
          className="mt-1 max-h-64 rounded-md border border-[var(--border)] object-contain"
        />
        {photo && (
          <button
            type="button"
            onClick={photo.onPick}
            disabled={photo.busy}
            title="Pick a new photo"
            aria-label="pick a new photo"
            className="absolute right-1 top-2 grid h-7 w-7 place-items-center rounded-full border border-[var(--border)] bg-[var(--bg)]/85 text-[var(--text-muted)] shadow-[var(--shadow-sm)] backdrop-blur hover:text-[var(--text)] disabled:opacity-50"
          >
            <RefreshIcon spinning={photo.busy} />
          </button>
        )}
      </span>
    );
  }
  if (!photo) return <span className="italic text-[var(--text-faint)]">empty</span>;
  return (
    <span className="inline-flex items-center gap-2" onMouseDown={(e) => e.stopPropagation()}>
      {photo.needsConnect ? (
        <a
          href="/google/connect"
          className="text-[0.85em] text-[var(--accent)] underline hover:opacity-90"
          title="Grant Rialto access to the Google Photos picker"
        >
          Connect Google Photos
        </a>
      ) : (
        <button
          type="button"
          onClick={photo.onPick}
          disabled={photo.busy}
          title="Add a photo from Google Photos"
          className="inline-flex items-center gap-1 rounded bg-[var(--bg-hover)] px-[6px] py-[1px] text-[0.8rem] text-[var(--accent)] hover:opacity-90 disabled:opacity-50"
        >
          <CameraIcon />
          {photo.busy ? 'Adding…' : 'Add photo'}
        </button>
      )}
      {photo.message && <span className="text-[0.8em] text-[var(--text-faint)]">{photo.message}</span>}
    </span>
  );
}

function CameraIcon() {
  return (
    <svg viewBox="0 0 16 16" width={13} height={13} fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="2" y="3.5" width="12" height="9" rx="1.5" />
      <circle cx="5.5" cy="6.5" r="1" />
      <path d="M3 12l3.5-3.5 2.5 2.5 2-2L14 11.5" />
    </svg>
  );
}

function RefreshIcon({ spinning }: { spinning?: boolean }) {
  return (
    <svg
      viewBox="0 0 16 16"
      width={14}
      height={14}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={spinning ? 'animate-spin' : undefined}
    >
      <path d="M13.5 8a5.5 5.5 0 1 1-1.6-3.9" />
      <path d="M13.5 2.5V5H11" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      width={14}
      height={14}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M11 2.5l2.5 2.5L6 12.5l-3 .5.5-3z" />
      <path d="M10 3.5l2.5 2.5" />
    </svg>
  );
}

function autosize(ta: HTMLTextAreaElement) {
  ta.style.height = 'auto';
  ta.style.height = ta.scrollHeight + 'px';
}

function range(start: number, end: number): number[] {
  const out: number[] = [];
  for (let i = start; i <= end; i++) out.push(i);
  return out;
}

function lineSpacing(line: string): string {
  if (/^#\s/.test(line)) return 'mt-5 mb-1';
  if (/^##\s/.test(line)) return 'mt-4 mb-1';
  if (/^#{3,6}\s/.test(line)) return 'mt-3 mb-[2px]';
  return '';
}

function unquote(s: string): string {
  return s.trim().replace(/^["']|["']$/g, '').trim();
}

function assetSrc(ref: string): string {
  const cleaned = unquote(ref.replace(/^!?\[\[/, '').replace(/\]\]$/, ''));
  if (/^https?:\/\//i.test(cleaned)) return cleaned;
  return `/api/notes/asset?ref=${encodeURIComponent(cleaned)}`;
}

// ---- segment + frontmatter parsing ----

type Segment =
  | { kind: 'line'; index: number }
  | { kind: 'frontmatter'; start: number; end: number }
  | { kind: 'callout'; start: number; end: number; title: string }
  | { kind: 'fence'; start: number; end: number };

const CALLOUT_RE = /^>\s*\[!(\w+)\][+-]?\s*(.*)$/;

function parseSegments(lines: string[]): Segment[] {
  const segs: Segment[] = [];
  const n = lines.length;
  let i = 0;

  // YAML frontmatter: a leading `---` with a matching closer.
  if (n > 1 && lines[0].trim() === '---') {
    let j = 1;
    while (j < n && lines[j].trim() !== '---') j++;
    if (j < n) {
      segs.push({ kind: 'frontmatter', start: 0, end: j });
      i = j + 1;
    }
  }

  for (; i < n; i++) {
    const line = lines[i];
    const co = CALLOUT_RE.exec(line);
    if (co) {
      let j = i;
      while (j + 1 < n && /^>/.test(lines[j + 1])) j++;
      segs.push({ kind: 'callout', start: i, end: j, title: co[2].trim() || co[1] });
      i = j;
      continue;
    }
    if (/^\s*```/.test(line)) {
      let j = i + 1;
      while (j < n && !/^\s*```/.test(lines[j])) j++;
      const end = j < n ? j : n - 1;
      segs.push({ kind: 'fence', start: i, end });
      i = end;
      continue;
    }
    segs.push({ kind: 'line', index: i });
  }
  return segs;
}

interface FrontmatterProp {
  key: string;
  value: string;
  lineIndex: number;
  items: string[];
}

// A deliberately small YAML reader — enough for the daily-note properties
// (scalars and one level of `- ` list items). Each prop remembers its source
// line so clicking a row drops the caret there in source mode.
function parseFrontmatter(lines: string[], start: number, end: number): FrontmatterProp[] {
  const props: FrontmatterProp[] = [];
  let cur: FrontmatterProp | null = null;
  for (let i = start; i <= end; i++) {
    const ln = lines[i];
    const kv = /^([\w][\w ./-]*?):\s*(.*)$/.exec(ln);
    const item = /^\s*-\s+(.*)$/.exec(ln);
    if (kv) {
      cur = { key: kv[1], value: kv[2], lineIndex: i, items: [] };
      props.push(cur);
    } else if (item && cur) {
      cur.items.push(unquote(item[1]));
    }
  }
  return props;
}
